/**
 * Components that render configuration options/settings for each
 * trigger event type.
 *
 * Note: Styling for these components is based on
 * that for CreateTriggerDrawer
 */
import {Select} from '@wandb/weave/components/Form/Select';
import {IconClose} from '@wandb/weave/components/Icon';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import {
  constFunction,
  opArtifactId,
  opArtifactName,
  opDict,
  opMap,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {
  TriggerScopeType,
  useProjectArtifactCollectionsQuery,
} from '../../../generated/graphql';
import {Key as ArtifactKey} from '../../../util/artifacts';
import {EMPTY_FILTER_VALUE} from '../../../util/filtersDefaults';
import {FilterKey, ValueFilter} from '../../../util/filterTypes';
import {useArtifactPortfoliosNode} from '../../ModelRegistry/utils';
import {createAliasFilter} from './aliasUtils';
import {
  EventStoreActions,
  isAliasEventConfiguredOk,
  TriggerRootFilter,
  useEventStore,
} from './CreateTriggerDrawer/EventStore';
import * as S from './TriggerEventConfiguration.styles';
import {OptionType} from './TriggersTable/utilTypes';

type EventConfigurationFilterProps = {
  descriptionString: string;
  index: number;
  filter: ValueFilter<FilterKey>;
  triggerScopeID?: string;
  setFilter(index: number, filter: ValueFilter<FilterKey>): void;
  setTriggerScopeID(val: string): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

type LinkModelFilterConfigurerProps = {
  index: number;
  filter: ValueFilter<FilterKey>;
  triggerScopeID?: string;
  setTriggerScopeID(val: string): void;
  setFilter(index: number, filter: ValueFilter<FilterKey>): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

export type EventConfigurationProps = {
  filters: TriggerRootFilter['filters'][0];
  artifactCollectionID?: string;
  triggerScopeType?: TriggerScopeType;
  triggerScopeID?: string;
  setFilter(i: number, filter: ValueFilter<FilterKey>): void;
  pushFilter(newFilter: ValueFilter<FilterKey>): void;
  deleteFilter(index: number): void;
  setTriggerScopeType?(val: TriggerScopeType): void;
  setTriggerScopeID(val: string | undefined): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

export const ArtifactAliasFilterCreator = ({
  filters: rootFilters,
  artifactCollectionID,
  setFilter,
  deleteFilter,
  setTriggerScopeType,
  setTriggerScopeID,
  triggerScopeType,
  triggerScopeID,
  setTriggerArtifactCollectionName,
}: EventConfigurationProps) => {
  const {projectID, getAllowCollectionSelection} = useEventStore(
    useShallow(s => s)
  );
  const [isAddCollectionEnabled, setIsAddCollectionEnabled] = useState(
    getAllowCollectionSelection() &&
      triggerScopeType === TriggerScopeType.ArtifactCollection
  );
  useEffect(() => {
    // The triggerScopeType/triggerScopeID must get set, so
    // when the user is not selecting a collection manually,
    // we need to set it for them
    if (!isAddCollectionEnabled || !getAllowCollectionSelection()) {
      if (artifactCollectionID) {
        setTriggerScopeType?.(TriggerScopeType.ArtifactCollection);
        setTriggerScopeID(artifactCollectionID);
      } else {
        setTriggerScopeType?.(TriggerScopeType.Project);
        setTriggerScopeID(projectID);
      }
    }

    const firstFilterIsValid =
      rootFilters.filters[0] && rootFilters.filters[0]?.value !== '';
    EventStoreActions.setIsAddAliasConfigurationOk(
      isAliasEventConfiguredOk(
        firstFilterIsValid,
        getAllowCollectionSelection(),
        isAddCollectionEnabled,
        triggerScopeID
      )
    );
  }, [
    artifactCollectionID,
    getAllowCollectionSelection,
    isAddCollectionEnabled,
    projectID,
    rootFilters.filters,
    setTriggerScopeID,
    setTriggerScopeType,
    triggerScopeID,
  ]);
  return (
    <>
      <S.LabelRow>
        <S.Label>Alias regex</S.Label>
      </S.LabelRow>
      <S.StyledTextField
        placeholder="Name or regex"
        value={(rootFilters.filters[0]?.value as string) ?? ''}
        onChange={value => {
          setFilter(0, createAliasFilter(value as string));
        }}
        autoFocus={true}
      />
      {getAllowCollectionSelection() && (
        <>
          {!isAddCollectionEnabled && rootFilters.filters[0] && (
            <S.AddFilter
              className="fake-link filter__new"
              data-test="filter-add"
              onClick={() => {
                setIsAddCollectionEnabled(true);
                if (setTriggerScopeType != null) {
                  setTriggerScopeType(TriggerScopeType.ArtifactCollection);
                  setTriggerScopeID(artifactCollectionID ?? '');
                }
              }}>
              Add collection filter
            </S.AddFilter>
          )}
          {isAddCollectionEnabled && (
            <>
              <S.LabelRow>
                <S.Label>Artifact collection</S.Label>
              </S.LabelRow>
              <S.InlineBlockAction>
                <S.InlineBlockActionLeft>
                  <ArtifactCollectionSelector
                    index={1}
                    descriptionString="Artifact collection"
                    filter={rootFilters.filters[1] ?? EMPTY_FILTER_VALUE}
                    setFilter={
                      /* TODO: it seems like this does nothing, so maybe have it throw an exception? */ (
                        index: number,
                        f: ValueFilter<ArtifactKey>
                      ) => {
                        setFilter(index, f);
                      }
                    }
                    setTriggerScopeID={setTriggerScopeID}
                    triggerScopeID={triggerScopeID}
                    setTriggerArtifactCollectionName={
                      setTriggerArtifactCollectionName
                    }
                  />
                </S.InlineBlockActionLeft>
                <S.InlineBlockActionRight>
                  <TooltipDeprecated
                    inverted
                    size="mini"
                    content="Remove filter"
                    position="right center"
                    on="hover"
                    trigger={
                      <S.DeleteFilter>
                        <IconClose
                          data-test="filter-delete"
                          onClick={() => {
                            setIsAddCollectionEnabled(false);
                            setTriggerScopeID(projectID);
                            if (setTriggerScopeType != null) {
                              setTriggerScopeType(TriggerScopeType.Project);
                              if (setTriggerArtifactCollectionName) {
                                setTriggerArtifactCollectionName('');
                              }
                            }

                            deleteFilter(1); // TODO: I'm pretty sure filter 1 is unused?
                          }}
                        />
                      </S.DeleteFilter>
                    }
                  />
                </S.InlineBlockActionRight>
              </S.InlineBlockAction>
            </>
          )}
        </>
      )}
    </>
  );
};

export const LinkModelFilterCreator = (props: EventConfigurationProps) => {
  const {getAllowCollectionSelection, projectID} = useEventStore(
    useShallow(s => s)
  );

  // These variables lets us avoid having props in the useEffect dependency array.
  const {artifactCollectionID, setTriggerScopeID, setTriggerScopeType} = props;
  useEffect(() => {
    // Do first time setup of the trigger scope type and ID. Necessary when
    // the user isn't selecting a collection manually (eg project level scope,
    // or when the collection is specified by the entry point).
    // Future improvement: move this logic up into the eventConfig store.
    if (artifactCollectionID != null) {
      setTriggerScopeType?.(TriggerScopeType.ArtifactCollection);
      setTriggerScopeID(artifactCollectionID);
    } else {
      setTriggerScopeType?.(TriggerScopeType.Project);
      setTriggerScopeID(projectID);
    }
    // note that right now these are all non-changing values, so this should not
    // get triggered more than once per run of the wizard
  }, [artifactCollectionID, projectID, setTriggerScopeID, setTriggerScopeType]);

  // we don't show any UI if we don't allow collection selection
  if (!getAllowCollectionSelection()) {
    return null;
  }
  return (
    <LinkModelFilterConfigurer
      {...props}
      index={0}
      setFilter={props.setFilter}
      filter={props.filters.filters[0]}
    />
  );
};

type artifactPortfolioInfo = {
  id: string;
  name: string;
};

const LinkModelFilterConfigurer = (props: LinkModelFilterConfigurerProps) => {
  const eventConfig = useEventStore(useShallow(s => s));
  const portfoliosNode = useArtifactPortfoliosNode(
    'model',
    eventConfig.entityName
  );
  const portfoliosDictNode = opMap({
    arr: portfoliosNode,
    mapFn: constFunction({row: 'artifact'}, ({row}) => {
      return opDict({
        id: opArtifactId({artifact: row}),
        name: opArtifactName({artifact: row}),
      } as any);
    }),
  });
  const p = useNodeValue(portfoliosDictNode);

  const options: OptionType[] = p?.result?.map(
    (port: artifactPortfolioInfo) => {
      return {
        value: port.id,
        label: port.name,
      };
    }
  );
  useEffect(() => {
    if (props.setTriggerArtifactCollectionName && props.triggerScopeID) {
      props.setTriggerArtifactCollectionName(
        options?.find(option => option.value === props.triggerScopeID)?.label ??
          ''
      );
    }
  });

  if (p.loading) {
    return null;
  }

  if (options.length === 0) {
    return <S.Label>No collections available.</S.Label>;
  }
  return (
    <>
      <S.LabelRow>
        <S.Label>Collection</S.Label>
      </S.LabelRow>
      <Select<OptionType | null>
        defaultValue={
          props.triggerScopeID
            ? {
                label:
                  options.find(opt => opt.value === props.triggerScopeID)
                    ?.label ?? '',
                value: props.triggerScopeID,
              }
            : null
        }
        options={options}
        onChange={(option: OptionType | null) => {
          if (option) {
            props.setTriggerScopeID(option.value);
            if (props.setTriggerArtifactCollectionName) {
              props.setTriggerArtifactCollectionName(option.label);
            }
          }
        }}
      />
    </>
  );
};

// note that this is used as the CreateArtifact filter creator
export const ArtifactCollectionFilterCreator = (
  props: EventConfigurationProps
) => {
  return (
    <>
      <S.LabelRow>
        <S.Label>Artifact collection</S.Label>
      </S.LabelRow>
      <ArtifactCollectionSelector
        {...props}
        index={0}
        setFilter={props.setFilter}
        filter={props.filters.filters[0]}
        descriptionString="Artifact collection"
        triggerScopeID={props.triggerScopeID}
        setTriggerScopeID={props.setTriggerScopeID}
        setTriggerArtifactCollectionName={
          props.setTriggerArtifactCollectionName
        }
      />
    </>
  );
};

export const ArtifactCollectionSelector = (
  props: EventConfigurationFilterProps
) => {
  const eventConfig = useEventStore(useShallow(s => s));
  const {data, loading} = useProjectArtifactCollectionsQuery({
    variables: {
      entityName: eventConfig.entityName ?? '',
      projectName: eventConfig.projectName ?? '',
    },
    skip: eventConfig.entityName == null || eventConfig.projectName == null,
  });
  const setTriggerScopeID = props.setTriggerScopeID;
  const setTriggerArtifactCollectionName =
    props.setTriggerArtifactCollectionName;

  const artifactCollections: OptionType[] =
    data?.project?.artifactTypes.edges
      .map(artifactTypeEdge => {
        return artifactTypeEdge.node?.artifactCollections?.edges.map(
          artifactCollectionEdge => {
            return {
              label: artifactCollectionEdge.node?.name as string,
              value: artifactCollectionEdge.node?.id as string,
            };
          }
        );
      })
      .flat()
      .filter((v): v is OptionType => v != null) ?? [];

  useEffect(() => {
    if (setTriggerArtifactCollectionName && props.triggerScopeID) {
      setTriggerArtifactCollectionName(
        artifactCollections.find(
          option => option.value === props.triggerScopeID
        )?.label ?? ''
      );
    }
  });
  if (loading || data == null) {
    return <div></div>;
  }
  return (
    <Select<OptionType | null>
      placeholder={`Select your ${props.descriptionString.toLowerCase()}`}
      defaultValue={
        props.triggerScopeID
          ? {
              label:
                artifactCollections.find(
                  ac => ac.value === props.triggerScopeID
                )?.label ?? '',
              value: props.triggerScopeID,
            }
          : null
      }
      options={artifactCollections}
      onChange={(option: OptionType | null) => {
        if (option) {
          setTriggerScopeID(option.value);
          if (setTriggerArtifactCollectionName) {
            setTriggerArtifactCollectionName(option.label);
          }
        }
      }}
    />
  );
};
